import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { CalendarIcon, Home, Hammer, Wrench, AlertTriangle, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { BOOKING } from "@/constants/testIds";
import { PhotoUpload } from "@/components/PhotoUpload";

const WEEKDAY_SLOTS = ["08:00","09:00","10:00","11:00","12:00","14:00","15:00","16:00","17:00"];
const SATURDAY_SLOTS = ["08:00","09:00","10:00","11:00","12:00"];
function slotsForDate(d) {
  if (!d) return WEEKDAY_SLOTS;
  const day = d.getDay(); // 0=dom, 6=sab
  if (day === 6) return SATURDAY_SLOTS;
  return WEEKDAY_SLOTS;
}
const REPAIR_TYPES = [
  "Scala – gradino/pedata",
  "Scala – ringhiera/corrimano",
  "Ringhiera esterna",
  "Infisso – finestra",
  "Infisso – porta",
  "Parquet – riparazione tavola",
  "Altro (descrivi nelle note)",
];

export default function Book() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState("sopralluogo");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [description, setDescription] = useState("");
  const [repairType, setRepairType] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const TIME_SLOTS_FOR_DATE = slotsForDate(date);

  useEffect(() => {
    if (!user) nav("/login", { state: { returnTo: "/prenota" } });
  }, [user, nav]);

  useEffect(() => { setPhone(user?.phone || ""); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Seleziona data e orario"); return; }
    setBusy(true);
    try {
      const payload = {
        service_type: service,
        date: format(date, "yyyy-MM-dd"),
        time_slot: time,
        phone,
        address: service === "sopralluogo" ? address : null,
        description,
        repair_type: service === "riparazione" ? repairType : null,
      };
      const { data } = await api.post("/bookings", payload);
      setSuccess(data);
      toast.success("Richiesta inviata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 bg-craft" data-testid={BOOKING.successMsg}>
        <div className="bg-white border border-border rounded-xl p-8 md:p-12 shadow-sm">
          <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-6">Richiesta inviata!</h1>
          <p className="text-foreground/70 mt-3">
            {success.status === "pending"
              ? "Ti risponderemo entro 24 ore con la conferma dell'intervento di riparazione."
              : "Il tuo appuntamento è confermato. Ti abbiamo inviato un'email con i dettagli."}
          </p>
          <div className="mt-6 rounded-lg bg-secondary p-4 text-sm">
            <div><span className="text-foreground/60">Servizio:</span> <b>{serviceLabel(success.service_type)}</b></div>
            <div><span className="text-foreground/60">Data:</span> <b>{success.date}</b> alle <b>{success.time_slot}</b></div>
            <div><span className="text-foreground/60">Stato:</span> <b className="capitalize">{success.status === "pending" ? "in attesa" : success.status === "approved" ? "confermato" : success.status}</b></div>
          </div>
          {success.service_type === "riparazione" && (
            <div className="mt-6 border-t border-border pt-6">
              <div className="font-display text-xl">Allega foto del problema</div>
              <div className="text-sm text-foreground/60 mt-1">Aiuta il nostro tecnico a valutare l'intervento prima del sopralluogo.</div>
              <PhotoUpload bookingId={success.booking_id} />
            </div>
          )}
          <div className="flex gap-3 mt-8">
            <Button onClick={() => nav("/le-mie-prenotazioni")} className="rounded-full">Vedi le mie prenotazioni</Button>
            <Button variant="outline" onClick={() => { setSuccess(null); setDate(null); setTime(""); setDescription(""); }} className="rounded-full">Nuova prenotazione</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 md:py-16">
      <div className="text-xs tracking-widest uppercase text-primary">Prenotazione</div>
      <h1 className="font-display text-3xl md:text-5xl mt-2">Fissiamo il tuo appuntamento.</h1>
      <p className="text-foreground/70 mt-3 max-w-xl">Scegli il tipo di servizio, la data e l'orario. Riceverai una conferma via email.</p>

      <form onSubmit={submit} className="bg-white border border-border rounded-xl p-6 md:p-8 mt-8 shadow-sm">
        <Tabs value={service} onValueChange={setService} data-testid={BOOKING.serviceTabs}>
          <TabsList className="grid grid-cols-3 w-full h-auto p-1">
            <TabsTrigger value="sopralluogo" data-testid={BOOKING.serviceTab("sopralluogo")} className="flex flex-col items-start gap-1 py-3 px-4 data-[state=active]:bg-white">
              <div className="flex items-center gap-2 text-sm font-semibold"><Home className="h-4 w-4"/> Sopralluogo</div>
              <div className="text-xs text-foreground/60">in cantiere</div>
            </TabsTrigger>
            <TabsTrigger value="showroom" data-testid={BOOKING.serviceTab("showroom")} className="flex flex-col items-start gap-1 py-3 px-4 data-[state=active]:bg-white">
              <div className="flex items-center gap-2 text-sm font-semibold"><Hammer className="h-4 w-4"/> Show room</div>
              <div className="text-xs text-foreground/60">nel nostro showroom</div>
            </TabsTrigger>
            <TabsTrigger value="riparazione" data-testid={BOOKING.serviceTab("riparazione")} className="flex flex-col items-start gap-1 py-3 px-4 data-[state=active]:bg-white">
              <div className="flex items-center gap-2 text-sm font-semibold"><Wrench className="h-4 w-4"/> Riparazione</div>
              <div className="text-xs text-foreground/60">su approvazione</div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="riparazione" className="mt-0">
            <Alert className="mt-6 bg-[#FFF4E5] border-[#F5A623] text-[#7A5211]" data-testid={BOOKING.banner60}>
              <AlertTriangle className="h-4 w-4 !text-[#F5A623]" />
              <AlertTitle className="text-[#7A5211] font-semibold">Costo di partenza 60€</AlertTitle>
              <AlertDescription className="text-[#7A5211]/90">
                In caso di prenotazione della riparazione, sarà calcolato un costo di partenza e chiamata di <b>60€</b>, da aggiungere poi all'eventuale lavoro.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <div>
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-start mt-1 h-11 rounded-md font-normal" data-testid={BOOKING.dateBtn}>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {date ? format(date, "PPP", { locale: it }) : "Seleziona una data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setTime(""); }}
                  locale={it}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Orario</Label>
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger className="mt-1 h-11" data-testid={BOOKING.timeSelect}>
                <SelectValue placeholder="Scegli un orario" />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOTS_FOR_DATE.map(t => (
                  <SelectItem key={t} value={t} data-testid={BOOKING.timeOption(t)}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-5">
          <div>
            <Label htmlFor="phone">Telefono</Label>
            <Input id="phone" required value={phone} onChange={(e)=>setPhone(e.target.value)} className="mt-1" data-testid={BOOKING.phoneInput} />
          </div>
          {service === "sopralluogo" && (
            <div>
              <Label htmlFor="address">Indirizzo cantiere</Label>
              <Input id="address" required value={address} onChange={(e)=>setAddress(e.target.value)} placeholder="Via, numero, città" className="mt-1" data-testid={BOOKING.addressInput} />
            </div>
          )}
          {service === "riparazione" && (
            <div>
              <Label>Tipo di riparazione</Label>
              <Select value={repairType} onValueChange={setRepairType}>
                <SelectTrigger className="mt-1 h-11" data-testid={BOOKING.repairTypeSelect}>
                  <SelectValue placeholder="Seleziona il tipo" />
                </SelectTrigger>
                <SelectContent>
                  {REPAIR_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {service === "riparazione" && (
          <div className="mt-5">
            <Label htmlFor="desc">Descrivi il problema</Label>
            <Textarea id="desc" rows={4} value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Che cosa è successo? Quando è iniziato? Foto in allegato via email dopo la conferma." className="mt-1" data-testid={BOOKING.descInput} />
          </div>
        )}

        {service !== "riparazione" && (
          <div className="mt-5">
            <Label htmlFor="desc2">Note (facoltativo)</Label>
            <Textarea id="desc2" rows={3} value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Vuoi aggiungere qualcosa?" className="mt-1" data-testid={BOOKING.descInput} />
          </div>
        )}

        <Button type="submit" disabled={busy} size="lg" className="w-full mt-8 rounded-full h-12" data-testid={BOOKING.submitBtn}>
          {busy ? "Invio in corso..." : service === "riparazione" ? "Invia richiesta" : "Conferma prenotazione"}
        </Button>
      </form>
    </div>
  );
}

function serviceLabel(s) {
  return { sopralluogo: "Sopralluogo in cantiere", showroom: "Appuntamento in show room", riparazione: "Intervento di riparazione" }[s] || s;
}
